/**
 * Maintenance history: summary (events, average MTBF between replacements of
 * the same equipment, last maintenance) and the timeline of every
 * subsystem's events newest first with type badges, technician, work order,
 * duration, cost and parts. The section renders inline; the dialog wraps it.
 */
import { useMemo } from 'react';
import { Dialog } from 'radix-ui';
import { ArrowUp, Calendar, ClipboardCheck, Clock, Cog, DollarSign, Hammer, Package, Replace, ScrollText, Search, TriangleAlert, User, X, type LucideIcon } from 'lucide-react';
import { Chip, IconButton, type Tone } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { useTwin } from '../../store';
import { EmptyState, Stat } from '../components/layout';
import { useAssetName } from '../hooks/useAssetName';
import { useNowMs } from '../hooks/useNowMs';
import { pathTo } from '../lib/cardTree';
import { fmtDate, fmtMoney } from '../lib/format';
import { collectEvents, eventTypeLabel, isRecent, summariseHistory } from '../lib/maintenanceHistory';
import { useTwinUi } from '../state';

const TYPE_ICON: Record<string, LucideIcon> = {
  replacement: Replace,
  repair: Hammer,
  inspection: Search,
  calibration: Cog,
  upgrade: ArrowUp,
  incident: TriangleAlert,
  maintenance: ClipboardCheck,
};

const TYPE_TONE: Record<string, Tone> = {
  replacement: 'live',
  repair: 'caution',
  inspection: 'neutral',
  calibration: 'neutral',
  upgrade: 'nominal',
  incident: 'warning',
  maintenance: 'neutral',
};

export function MaintenanceHistorySection({ onClose }: { onClose?: () => void }) {
  const element = useTwin((s) => s.element);
  const select = useUI((s) => s.select);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const setTab = useTwinUi((s) => s.setTab);
  const now = useNowMs(60_000);
  const events = useMemo(() => (element ? collectEvents(element) : []), [element]);
  const summary = useMemo(() => summariseHistory(events, now), [events, now]);

  if (!element) return <EmptyState title="No twin loaded." />;
  if (events.length === 0) return <EmptyState title="No maintenance events recorded for this element." />;

  const goTo = (id: string) => {
    setExpanded(pathTo(element.subsystems, id).map((s) => s.id), true);
    select({ subsystemId: id });
    setTab('health');
    onClose?.();
  };

  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label="Total events" value={summary.total} />
        <Stat label="Avg MTBF" value={summary.avgMtbfDays !== null ? `${summary.avgMtbfDays} d` : 'N/A'} />
        <Stat label="Last maintenance" value={summary.lastMs !== null ? fmtDate(summary.lastMs) : 'N/A'} />
        <Stat label="Recorded cost" value={fmtMoney(summary.totalCost)} />
      </div>
      <div className="text-[10px] text-ink-3">
        {summary.replacements} replacements, {summary.recent} events in the last year. MTBF is the mean gap between replacements of the same equipment.
      </div>
      <ol className="relative flex flex-col gap-2 border-l border-hairline pl-4">
        {events.map((e, i) => {
          const Icon = TYPE_ICON[e.type] ?? ClipboardCheck;
          const recent = isRecent(e.dateMs, now);
          return (
            <li key={`${e.subsystemId}-${e.dateMs}-${i}`} className="relative">
              <span className={cn('absolute -left-[23px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border bg-raised', recent ? 'border-accent text-accent' : 'border-hairline-strong text-ink-3')}>
                <Icon className="h-2.5 w-2.5" />
              </span>
              <div className={cn('rounded-control border px-3 py-2', recent ? 'border-hairline-strong bg-raised' : 'border-hairline bg-raised/60')}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[11px] text-ink-3">{fmtDate(e.dateMs)}</span>
                  <Chip tone={TYPE_TONE[e.type] ?? 'neutral'} className="px-1.5 py-0 text-[9px]">
                    {eventTypeLabel(e.type)}
                  </Chip>
                  {recent && <span className="text-[9px] uppercase tracking-wider text-accent">recent</span>}
                </div>
                <button type="button" onClick={() => goTo(e.subsystemId)} className="mt-0.5 text-left text-ink hover:text-accent" title={e.path}>
                  {e.equipment}
                </button>
                {e.description && <div className="leading-snug text-ink-2">{e.description}</div>}
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-3">
                  {e.technician && (
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {e.technician}
                    </span>
                  )}
                  {e.workOrder && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <ClipboardCheck className="h-3 w-3" /> {e.workOrder}
                    </span>
                  )}
                  {e.duration && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {e.duration}
                    </span>
                  )}
                  {e.cost !== null && (
                    <span className="inline-flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> {fmtMoney(e.cost)}
                    </span>
                  )}
                  {e.parts.length > 0 && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Package className="h-3 w-3" /> {e.parts.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="text-[10px] text-ink-3">
        <Calendar className="mr-1 inline h-3 w-3" />
        Events come from the element JSON; they are static until a work-order feed exists.
      </div>
    </div>
  );
}

export function MaintenanceHistoryDialog() {
  const open = useTwinUi((s) => s.dialog === 'history');
  const closeDialog = useTwinUi((s) => s.closeDialog);
  const name = useAssetName();
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && closeDialog()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content aria-describedby={undefined} className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-32px)] w-[min(760px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none">
          <div className="flex h-11 shrink-0 items-center justify-between border-b border-hairline px-4">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-ink">
              <ScrollText className="h-4 w-4 text-accent" /> Maintenance history
              {name && <span className="font-mono text-[11px] font-normal text-ink-3">{name.title}</span>}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close (Esc)">
                <X className="h-4 w-4" />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 scrollbar-thin">
            <MaintenanceHistorySection onClose={closeDialog} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
